import { useQuery } from '@tanstack/react-query'
import type { DocumentDef, DocumentStatus } from '@project/contracts'
import { Button, Empty, Panel, Table, type ColumnDef as TableColumn } from '../design/index.js'
import { useApi, type DocumentDetail, type RelatedDoc } from '../app/api.js'
import { navigate, paths } from '../app/router.js'
import { useDocumentMenu } from './queries.js'
import { ErrorBlock, Loading } from './feedback.js'

const RANK: Record<DocumentStatus, number> = { DRAFT: 0, IN_REVIEW: 1, APPROVED: 2, CANCELLED: -1 }

const columns: TableColumn<RelatedDoc>[] = [
  { key: 'documentNo', header: 'Document no', type: 'docno', width: 180 },
  { key: 'documentStatus', header: 'Status', type: 'status', width: 150 },
  { key: 'docTypeLabel', header: 'Type' },
  { key: 'date', header: 'Date', type: 'date', width: 140 },
  { key: 'createdBy', header: 'Created by', width: 200 },
]

export interface RelatedTabProps {
  readonly def: DocumentDef
  readonly doc: DocumentDetail
  readonly companyId: string
}

export const RelatedTab = ({ def, doc, companyId }: RelatedTabProps) => {
  const api = useApi()
  const menu = useDocumentMenu()
  const related = useQuery({
    queryKey: ['related', api.companyId, def.key, doc.id],
    queryFn: () => api.related(def.key, doc.id),
  })

  const routeFor = (docType: string): string | undefined => menu.data?.find((m) => m.key === docType)?.route

  const offered = (def.createsNext ?? []).filter((next) => {
    if (RANK[doc.documentStatus] < 0) return false
    return next.whenStatus === undefined || RANK[doc.documentStatus] >= RANK[next.whenStatus]
  })

  const rows = related.data ?? []

  return (
    <>
      <Panel title="Create the next document">
        {offered.length === 0 ? (
          <p className="helper">
            Nothing can be raised from this document yet. The options appear once it has been approved.
          </p>
        ) : (
          <div className="row row--wrap">
            {offered.map((next) => {
              const route = routeFor(next.docType)
              return (
                <Button
                  key={next.docType}
                  kind="ghost"
                  disabled={route === undefined}
                  onClick={() => {
                    if (route !== undefined) navigate(paths.create(companyId, route, { from: doc.id, fromType: def.key }))
                  }}
                >
                  {next.label}
                </Button>
              )
            })}
          </div>
        )}
      </Panel>

      <Panel title="Linked documents" aside={rows.length > 0 ? `${rows.length} document${rows.length === 1 ? '' : 's'}` : undefined}>
        {related.isLoading ? (
          <Loading />
        ) : related.error ? (
          <ErrorBlock error={related.error} />
        ) : rows.length === 0 ? (
          <Empty
            title="No related documents created yet."
            body="Anything raised from this document, and anything it was raised from, is listed here."
          />
        ) : (
          <Table
            columns={columns}
            rows={rows}
            getKey={(row) => `${row.docType}-${row.id}`}
            onRowClick={(row) => navigate(paths.document(companyId, row.route, row.id))}
          />
        )}
      </Panel>
    </>
  )
}
